import {
  Alert, LinearProgress, Paper, Skeleton, Table, TableBody, TableCell, TableHead,
  TablePagination, TableRow, TableSortLabel, Typography,
} from '@mui/material';
import { TABLE_MAX_HEIGHT } from '../../theme';
import StickyTableContainer from './StickyTableContainer';

const PAGE_SIZES = [10, 25, 50, 100];

/**
 * Server side paginated, sortable table.
 * columns: [{ id, label, align, sort (ordering key, defaults to id; false to disable), render(row), width }]
 * ordering: the API ordering ('-spend'); page is 1-based. Rows stay visible while the next page loads.
 */
export default function DataTable({
  columns, rows, count, loading, error, page, pageSize, ordering, onOrderingChange, onPageChange, onPageSizeChange,
  onRowClick, emptyTitle = 'Nothing found', emptyHint, minWidth = 900, label,
}) {
  const descending = ordering?.startsWith('-');
  const orderKey = ordering?.replace(/^-/, '');
  const firstLoad = loading && rows.length === 0;

  const sortBy = (key) => {
    // numbers read best largest first; text A to Z
    if (key === orderKey) onOrderingChange(descending ? key : `-${key}`);
    else onOrderingChange(columns.find((column) => sortKey(column) === key)?.align === 'right' ? `-${key}` : key);
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', position: 'relative' }}>
      {error && <Alert severity="error" sx={{ borderRadius: 0 }}>{error}</Alert>}
      {/* over the header's top edge, so it takes no space and the table does not shift */}
      {loading && !firstLoad && (
        <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, zIndex: 4 }} />
      )}
      <StickyTableContainer sx={{ maxHeight: TABLE_MAX_HEIGHT }}>
        <Table stickyHeader size="small" sx={{ minWidth }} aria-label={label} aria-busy={loading}>
          <TableHead>
            <TableRow sx={{ '& th': { py: 1.25 } }}>
              {columns.map((column) => {
                const key = sortKey(column);
                return (
                  <TableCell key={column.id} align={column.align} sx={{ width: column.width }}
                             sortDirection={key && key === orderKey ? (descending ? 'desc' : 'asc') : false}>
                    {key ? (
                      <TableSortLabel active={key === orderKey} onClick={() => sortBy(key)}
                                      direction={key === orderKey && descending ? 'desc' : 'asc'}>
                        {column.label}
                      </TableSortLabel>
                    ) : column.label}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody sx={{ opacity: loading && !firstLoad ? 0.6 : 1, transition: 'opacity 0.2s' }}>
            {firstLoad ? [...Array(5)].map((_, index) => (
              <TableRow key={index}>
                {columns.map((column) => <TableCell key={column.id}><Skeleton /></TableCell>)}
              </TableRow>
            )) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 7, borderBottom: 0 }}>
                  <Typography sx={{ fontWeight: 500 }}>{emptyTitle}</Typography>
                  {emptyHint && <Typography variant="body2" color="text.secondary">{emptyHint}</Typography>}
                </TableCell>
              </TableRow>
            ) : rows.map((row) => (
              <TableRow key={row.id} hover onClick={onRowClick && (() => onRowClick(row))}
                        sx={{ cursor: onRowClick ? 'pointer' : 'default', '& td': { py: 1.25 } }}>
                {columns.map((column) => (
                  <TableCell key={column.id} align={column.align}
                             sx={{ whiteSpace: column.align === 'right' ? 'nowrap' : undefined }}>
                    {column.render ? column.render(row) : row[column.id] ?? '—'}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </StickyTableContainer>
      <TablePagination component="div" count={count} page={Math.max(page - 1, 0)} rowsPerPage={pageSize}
                       rowsPerPageOptions={PAGE_SIZES} onPageChange={(_, value) => onPageChange(value + 1)}
                       onRowsPerPageChange={(event) => onPageSizeChange(Number(event.target.value))}
                       sx={{ borderTop: 1, borderColor: 'divider' }} />
    </Paper>
  );
}

function sortKey(column) {
  return column.sort === false ? null : column.sort ?? column.id;
}
